import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import { formatOpeningBalance } from "../../helpers/financeAccounts";
import { loadFinancialPosition } from "../../helpers/financePosition";
import { FINANCE_POSITION_CHANGED_EVENT } from "../../helpers/financeStateEvents";
import { CAPABILITY } from "../../helpers/permissions";
import { visibleFinanceWorkspaceLinks } from "../../navigation/financeWorkspace";
import { useHardwareBackHandler } from "../../navigation/HardwareBackContext";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { usePermissions } from "../../hooks/usePermissions";
import { FinanceDashboardDesktop } from "./FinanceDesktop";
import { FinanceWorkspaceNav } from "./FinanceWorkspaceNav";
import { CrewPilotNavSectionHeader } from "../CrewPilotNavRow";
import { FinanceAccountsSummary } from "./FinanceAccountsSummary";
import { FinancialPositionCard } from "./FinancialPositionCard";
import {
  FinanceBackButton,
  FinanceComingSoonCard,
  FinanceMetricCard,
  FINANCE_HEADER_STYLE,
  FINANCE_PRIMARY_BTN_STYLE,
  FINANCE_SCREEN_STYLE,
  FINANCE_SECONDARY_BTN_STYLE,
  FINANCE_SUBTITLE_STYLE,
  FINANCE_TITLE_STYLE,
  FINANCE_TOOLBAR_STYLE,
} from "./financeUi";

const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "14px",
};

/**
 * Finance dashboard — cash position + account summary + workflow entry points.
 * Position / balances come only from the shared Financial Position service.
 */
export function FinanceDashboardScreen({
  currentUser,
  onBack,
  onOpenAccounts,
  onOpenActivity,
  onOpenMoneyIn,
  onOpenMoneyOut,
  onOpenTransfer,
  onOpenExpected,
  onOpenWorkspace,
}) {
  const isDesktop = useIsDesktopViewport();
  const { can, actor, orgPolicies } = usePermissions();
  const showOverview = can(CAPABILITY.SCREEN_FINANCE);
  const workspaceLinks = useMemo(
    () => visibleFinanceWorkspaceLinks(actor, orgPolicies),
    [actor, orgPolicies],
  );
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useHardwareBackHandler(
    useCallback(() => {
      if (typeof onBack !== "function") return false;
      onBack();
      return true;
    }, [onBack]),
  );

  const load = useCallback(async () => {
    if (!showOverview || shouldSkipSupabase(currentUser)) {
      setPosition(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const snapshot = await loadFinancialPosition(supabase);
      setPosition(snapshot);
      setError(null);
    } catch (err) {
      console.error("[Finance] position load failed:", err);
      setError("Unable to load cash position.");
      setPosition(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser, showOverview]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const onRefresh = () => {
      void load();
    };
    window.addEventListener(FINANCE_POSITION_CHANGED_EVENT, onRefresh);
    return () =>
      window.removeEventListener(FINANCE_POSITION_CHANGED_EVENT, onRefresh);
  }, [load]);

  const currency = position?.currency || "CAD";
  const expectedValue =
    position?.expectedCash === undefined || position?.expectedCash === null
      ? formatOpeningBalance(0, currency)
      : formatOpeningBalance(position.expectedCash, currency);

  if (isDesktop) {
    return (
      <FinanceDashboardDesktop
        position={position}
        loading={loading}
        error={error}
        currentUser={currentUser}
        onOpenAccounts={onOpenAccounts}
        onOpenActivity={onOpenActivity}
        onOpenMoneyIn={onOpenMoneyIn}
        onOpenMoneyOut={onOpenMoneyOut}
        onOpenTransfer={onOpenTransfer}
        onOpenExpected={onOpenExpected}
        showOverview={showOverview}
        workspaceNav={
          <FinanceWorkspaceNav links={workspaceLinks} onOpen={onOpenWorkspace} />
        }
      />
    );
  }

  return (
    <div style={FINANCE_SCREEN_STYLE}>
      <FinanceBackButton onClick={onBack} primary />

      <div style={FINANCE_HEADER_STYLE}>
        <p
          style={{
            margin: 0,
            fontSize: "11px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(78,226,161,0.85)",
            fontWeight: 700,
          }}
        >
          Financial Operations
        </p>
        <h1 style={FINANCE_TITLE_STYLE}>Finance</h1>
        <p style={FINANCE_SUBTITLE_STYLE}>
          {showOverview
            ? "Where is your money, what moved, and what can you safely spend."
            : "Open a finance workspace."}
        </p>
      </div>

      {showOverview ? (
        <div style={{ marginBottom: "14px" }}>
          <FinancialPositionCard
            position={position}
            loading={loading}
            error={error}
            title="Cash Position"
            showOpenHint={false}
            showAccountSummary={false}
          />
        </div>
      ) : null}

      <FinanceWorkspaceNav links={workspaceLinks} onOpen={onOpenWorkspace} />

      {showOverview ? (
      <>
      <CrewPilotNavSectionHeader>Actions</CrewPilotNavSectionHeader>
      <div style={FINANCE_TOOLBAR_STYLE}>
        <button
          type="button"
          style={FINANCE_PRIMARY_BTN_STYLE}
          onClick={onOpenMoneyIn}
        >
          Money In
        </button>
        <button
          type="button"
          style={FINANCE_SECONDARY_BTN_STYLE}
          onClick={onOpenMoneyOut}
        >
          Money Out
        </button>
        <button
          type="button"
          style={FINANCE_SECONDARY_BTN_STYLE}
          onClick={onOpenTransfer}
        >
          Transfer
        </button>
        <button
          type="button"
          style={FINANCE_SECONDARY_BTN_STYLE}
          onClick={onOpenActivity}
        >
          Activity
        </button>
        <button
          type="button"
          style={FINANCE_SECONDARY_BTN_STYLE}
          onClick={onOpenAccounts}
        >
          Manage Accounts
        </button>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <FinanceAccountsSummary
          accounts={position?.accounts || []}
          currency={currency}
          loading={loading}
          variant="full"
          reorderable
          currentUser={currentUser}
          onManageAccounts={onOpenAccounts}
          title="Accounts"
        />
      </div>

      <div style={GRID_STYLE}>
        <button
          type="button"
          onClick={onOpenActivity}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(78,226,161,0.35)",
            borderRadius: "14px",
            padding: "16px",
            textAlign: "left",
            color: "white",
            cursor: "pointer",
          }}
        >
          <h3
            style={{
              fontSize: "15px",
              fontWeight: 700,
              letterSpacing: "0.02em",
              margin: "0 0 8px",
            }}
          >
            Activity
          </h3>
          <p
            style={{
              fontSize: "13px",
              color: "rgba(255,255,255,0.55)",
              margin: "0 0 8px",
            }}
          >
            Full history of money in, money out, and transfers.
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "#4ee2a1",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Open →
          </p>
        </button>

        <button
          type="button"
          onClick={onOpenTransfer}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(78,226,161,0.35)",
            borderRadius: "14px",
            padding: "16px",
            textAlign: "left",
            color: "white",
            cursor: "pointer",
          }}
        >
          <h3
            style={{
              fontSize: "15px",
              fontWeight: 700,
              letterSpacing: "0.02em",
              margin: "0 0 8px",
            }}
          >
            Transfer
          </h3>
          <p
            style={{
              fontSize: "13px",
              color: "rgba(255,255,255,0.55)",
              margin: "0 0 8px",
            }}
          >
            Move money between accounts (for example, tax set-aside).
          </p>
          <p
            style={{
              fontSize: "13px",
              color: "#4ee2a1",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Open →
          </p>
        </button>

        <FinanceMetricCard
          title="Expected"
          value={expectedValue}
          subtext="From unpaid sent invoices"
          loading={loading}
          error={error}
          onClick={onOpenExpected}
        />
        <FinanceComingSoonCard
          title="Committed"
          description="Money already spoken for (upcoming bills and obligations)."
        />
        <FinanceComingSoonCard
          title="Upcoming Bills"
          description="Scheduled outflows and payment reminders."
        />
      </div>
      </>
      ) : null}
    </div>
  );
}
